import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useUrlTab from '../hooks/useUrlTab';
import marketplaceService from '../services/marketplaceService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import marketplaceExtrasService from '../services/marketplaceExtrasService';
import marketplaceActionTypesService from '../services/marketplaceActionTypesService';
import processingStepsService from '../services/processingStepsService';
import ConfirmModal from './shared/ConfirmModal';
import './ConnectorForm.css';
import CurrencySelect from './CurrencySelect';

// Authentication schemes a system can declare on its Adapter tab. `value` is the code stored in
// marketplace.authentication_scheme (V471, default NONE since V472); `label` is display only. NONE comes
// first because it is the default: no system is assumed to authenticate until somebody says it does.
const AUTHENTICATION_SCHEMES = [
  { value: 'NONE', label: 'None' },
  { value: 'API_KEY', label: 'API KEY' },
  // The proxy authenticates with a credential already held on the hub (entity_credentials) rather than
  // with a key minted for it — the system's own username and password, OAuth client or token, whichever
  // that credential holds. Nothing is generated per proxy in this case.
  { value: 'HUB_CREDENTIALS', label: 'Stored Credentials on Hub' },
  // THE OTHER DIRECTION (V513). Every scheme above answers "how does the hub sign in to this system".
  // This one says the system signs in to US — an MCP source is called by a model, not called out to —
  // so nothing is minted and no credential of theirs is held. What it needs instead is connection
  // details to be GIVEN: the endpoint, the grant, the app client and the scope, which are the Inbound
  // settings on its marketplace record.
  { value: 'INBOUND_OAUTH', label: 'Caller signs in to us (OAuth)' },
];

// How data moves between the system and the hub, one list per direction (V488). Codes are stored in
// marketplace.marketplace_to_hub / hub_to_marketplace; labels are display only. None first: the default.
const MARKETPLACE_TO_HUB = [
  { value: 'NONE', label: 'None' },
  { value: 'HTTP_PUSH', label: 'HTTP Push' },
  // THE HUB DIALS OUT AND THE DATA FLOWS IN (V520) — the hub fetches from the system: Opera Cloud's business events,
  // the Crawler reading a hotel's website. Stored as HTTP_POLL, which V520 defined for OHIP and only ever set by SQL,
  // so the screen could not show or choose it. Not Hub to Marketplace's HTTP Pull: that is the SYSTEM collecting
  // from us (OXI's GET /oxi/{key}).
  { value: 'HTTP_POLL', label: 'HTTP Pull (the hub fetches from the system)' },
];
const HUB_TO_MARKETPLACE = [
  { value: 'NONE', label: 'None' },
  { value: 'HTTP_PULL', label: 'HTTP Pull' },
  // THE SYSTEM COLLECTS ITS OWN, from the shared Universal API queue (V516). Distinct from HTTP Pull:
  // that is a system calling an endpoint of its own shape (OXI collecting from /oxi/{key}); this is a
  // Universal API client polling GET /mq with the credential it already has. A vendor cannot speak the
  // first, so the two must not be read as the same answer.
  { value: 'MQ_POLL', label: 'Polls the Universal API queue (/mq)' },
];

// HOW A TARGET'S RESULT IS LOOKED AT ON THE PIPELINE BOARD (V583). Stored in marketplace.result_viewer; '' is
// none. A system whose result is its contents rather than an answer (Azure Blob stores what it is given) gets
// a button on its Target card that opens the viewer named here.
const RESULT_VIEWERS = [
  { value: '', label: 'None' },
  { value: 'FILE_VIEWER', label: 'File viewer' },
];

// Every tab's ?tab= name. A name that is not here falls back to the first. The tabs that need a saved record exist only
// in edit mode, so create mode offers fewer.
const MARKETPLACE_TABS = ['general', 'company', 'description', 'settings', 'marketplacesettings', 'modules', 'esb',
  'apidoc', 'actions', 'transactionfees'];
const MARKETPLACE_CREATE_TABS = ['general', 'company', 'description', 'settings', 'transactionfees'];

function MarketplaceForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    SYSTEMNAME: '',
    SYSTEMROLE: '',
    COMPANYNAME: '',
    COMPANYWEB: '',
    COMPANYSUPPORTEMAIL: '',
    COMPANYSUPPORTWEB: '',
    SYSTEMACTIVE: 'Y',
    SYSTEMIMAGEURL: '',
    SYSTEMURLPARAMETER: '',
    SYSTEMSTATUS: 'PENDING',
    SYSTEMDESCRIPTION: '',
    SYSTEMSUBTITLE: '',
    ALLOWTEST: 'N',
    IS_CLOUD_TESTABLE: 'N',
    ALLOWCREATE: 'N',
    HASSETTINGS: 'N',
    USES_API_KEY: 'N',
    uapi_enabled: 'N',
    IS_VENDOR: 'N',
    is_integration_source: 'N',
    uses_proxy: 'N',
    has_file_viewer: 'N',
    is_websocket_client: 'N',
    uses_streaming_client: 'N',
    streaming_container: '',
    uses_event_poller: 'N',
    run_now_button: 'N',
    has_pipeline_conversions: 'N',
    polling_implementation_id: '',
    proxy_name: '',
    authentication_scheme: 'NONE',
    marketplace_to_hub: 'NONE',
    hub_to_marketplace: 'NONE',
    result_viewer: '',
    syncconnect_repo_ref: '',
    LATESTVERSION: '',
    payment_porter: 'N',
    has_enqueue: 'N',
    has_dequeue: 'N',
    api_transactions_per_unit: 10000,
    api_cost_per_unit: 0,
    api_cost_currency: 'EUR'
  });

  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  // In the URL (?tab=…) so the screen can be linked to and reloaded on the tab it was on. A tab handed over in
  // navigation state (Marketplace Settings' back link) still works, as the fallback.
  const [activeTab, setActiveTab] = useUrlTab(isEditMode ? MARKETPLACE_TABS : MARKETPLACE_CREATE_TABS, location.state?.tab);
  // SAVING, NOT LOADING. `loading` swaps the whole form for "Loading..." in edit mode; an update keeps the
  // form — and the tab it was pressed on — on screen, so it has its own flag and a confirmation line.
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // State for marketplace settings
  const [marketplaceSettings, setMarketplaceSettings] = useState([]);

  // Modules and API documentation links. A module is a Universal API resource path (/rsv, /crm, ...)
  // this system can serve — see V416; the free-typed name it used to be could not be matched to a route.
  const [modules, setModules] = useState([]);
  const [uapiRoutes, setUapiRoutes] = useState([]);
  const [newModuleName, setNewModuleName] = useState('');
  const [newModuleRouteId, setNewModuleRouteId] = useState('');
  // ESB attributes — what this system brings to an integration (V467). Read by the ESB screen when it
  // composes one, so they are facts about the SYSTEM rather than about any integration that uses it.
  const [esbAttributes, setEsbAttributes] = useState([]);
  const [newEsbAttribute, setNewEsbAttribute] = useState({ attribute: '', value: '', direction: 'BOTH', notes: '' });
  const [apiDocs, setApiDocs] = useState([]);
  const [newApiDoc, setNewApiDoc] = useState({ label: '', doc_type: 'url', location: '', content: '' });
  // Which document is open for editing, and its working text. A document lives in the row (doc_type
  // 'markdown'), not behind a link, so it is edited here rather than wherever a URL points.
  const [openDoc, setOpenDoc] = useState(null);
  const [docText, setDocText] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  const [actions, setActions] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [newAction, setNewAction] = useState({ marketplaceactiontype_id: '', actiondescription: '', marketplaceactionstatus: 'ACTIVE' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Action-code lookup, grouped by category for the dropdown.
  useEffect(() => {
    marketplaceActionTypesService.list()
      .then(res => setActionTypes(res.actionTypes || []))
      .catch(() => setActionTypes([]));
  }, []);

  // The processing steps (admin › Processing Steps) that the Polling Implementation select on the Adapter tab offers:
  // a resync is run by one of these, whose name is its Step Functions state machine. Loaded once; a system with no
  // resync simply leaves the select on "None".
  const [processingSteps, setProcessingSteps] = useState([]);
  useEffect(() => {
    processingStepsService.list()
      .then(rows => setProcessingSteps(Array.isArray(rows) ? rows : []))
      .catch(() => setProcessingSteps([]));
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      fetchMarketplaceItem();
    }
  }, [id, isEditMode]);

  const fetchModules = async () => {
    try {
      const res = await marketplaceExtrasService.modules.list(id);
      setModules(res.modules || []);
      // The pickable routes come back on the same response, so the select can never offer a route the
      // server would reject.
      setUapiRoutes(res.routes || []);
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  const fetchEsbAttributes = async () => {
    try {
      const res = await marketplaceExtrasService.esb.list(id);
      setEsbAttributes(res.attributes || []);
    } catch (err) {
      console.error('Error fetching ESB attributes:', err);
    }
  };

  // ADDS AND CORRECTS WITH ONE CALL. A row is keyed on its attribute and direction, so re-entering an
  // attribute replaces it — the screen never has to decide whether it is editing or adding, and cannot
  // get that decision wrong.
  const saveEsbAttribute = async (attr) => {
    const name = String(attr.attribute || '').trim();
    if (!name) return;
    try {
      const res = await marketplaceExtrasService.esb.save(id, {
        attribute: name,
        value: attr.value,
        direction: attr.direction || 'BOTH',
        notes: attr.notes,
      });
      // The whole list comes back, because a save may have replaced a row rather than added one.
      setEsbAttributes(res.attributes || []);
      setNewEsbAttribute({ attribute: '', value: '', direction: 'BOTH', notes: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save that ESB attribute');
    }
  };

  const removeEsbAttribute = async (attributeId) => {
    try {
      await marketplaceExtrasService.esb.remove(id, attributeId);
      fetchEsbAttributes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove that ESB attribute');
    }
  };

  const fetchApiDocs = async () => {
    try {
      const res = await marketplaceExtrasService.apiDocs.list(id);
      setApiDocs(res.apiDocs || []);
    } catch (err) {
      console.error('Error fetching API docs:', err);
    }
  };

  const addModule = async () => {
    if (!newModuleRouteId) return;
    try {
      // name omitted when blank so the lambda applies the path as the label — one default, server-side.
      await marketplaceExtrasService.modules.create(id, {
        uapi_route_id: newModuleRouteId,
        ...(newModuleName.trim() ? { name: newModuleName.trim() } : {}),
      });
      setNewModuleName('');
      setNewModuleRouteId('');
      fetchModules();
      fetchEsbAttributes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add module');
    }
  };

  // Save an open document. Sends the row's own label and type back untouched — this screen edits the
  // TEXT, and a save that also rewrote the label would undo a rename someone made on purpose.
  const saveOpenDoc = async () => {
    if (!openDoc) return;
    setSavingDoc(true);
    try {
      await marketplaceExtrasService.apiDocs.update(id, openDoc.id, {
        label: openDoc.label, doc_type: 'markdown', location: '', content: docText,
      });
      setOpenDoc(null);
      fetchApiDocs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the document');
    } finally {
      setSavingDoc(false);
    }
  };

  const addApiDoc = async () => {
    // A link needs a location; a document needs content. Requiring both of either one is what stopped
    // a document being addable at all.
    const wantsDoc = newApiDoc.doc_type === 'markdown';
    if (!newApiDoc.label.trim()) return;
    if (wantsDoc ? !newApiDoc.content.trim() : !newApiDoc.location.trim()) return;
    try {
      await marketplaceExtrasService.apiDocs.create(id, newApiDoc);
      setNewApiDoc({ label: '', doc_type: 'url', location: '' });
      fetchApiDocs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add API doc');
    }
  };

  const fetchActions = async () => {
    try {
      const res = await marketplaceExtrasService.actions.list(id);
      setActions(res.actions || []);
    } catch (err) {
      console.error('Error fetching actions:', err);
    }
  };

  const addAction = async () => {
    if (!newAction.marketplaceactiontype_id) return;
    try {
      await marketplaceExtrasService.actions.create(id, newAction);
      setNewAction({ marketplaceactiontype_id: '', actiondescription: '', marketplaceactionstatus: 'ACTIVE' });
      fetchActions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add action');
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { kind, id: itemId } = confirmDelete;
    setConfirmDelete(null);
    try {
      if (kind === 'module') {
        await marketplaceExtrasService.modules.remove(id, itemId);
        fetchModules();
      } else if (kind === 'action') {
        await marketplaceExtrasService.actions.remove(id, itemId);
        fetchActions();
      } else {
        await marketplaceExtrasService.apiDocs.remove(id, itemId);
        fetchApiDocs();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const fetchMarketplaceItem = async () => {
    try {
      setLoading(true);
      const data = await marketplaceService.getSettingById(id);
      setFormData({
        SYSTEMNAME: data.SYSTEMNAME || '',
        SYSTEMROLE: data.SYSTEMROLE || '',
        COMPANYNAME: data.COMPANYNAME || '',
        COMPANYWEB: data.COMPANYWEB || '',
        COMPANYSUPPORTEMAIL: data.COMPANYSUPPORTEMAIL || '',
        COMPANYSUPPORTWEB: data.COMPANYSUPPORTWEB || '',
        SYSTEMACTIVE: data.SYSTEMACTIVE || 'Y',
        SYSTEMIMAGEURL: data.SYSTEMIMAGEURL || '',
        SYSTEMURLPARAMETER: data.SYSTEMURLPARAMETER || '',
        SYSTEMSTATUS: data.SYSTEMSTATUS || 'PENDING',
        SYSTEMDESCRIPTION: data.SYSTEMDESCRIPTION || '',
        SYSTEMSUBTITLE: data.SYSTEMSUBTITLE || '',
        ALLOWTEST: data.ALLOWTEST || 'N',
        IS_CLOUD_TESTABLE: data.IS_CLOUD_TESTABLE || 'N',
        ALLOWCREATE: data.ALLOWCREATE || 'N',
        HASSETTINGS: data.HASSETTINGS || 'N',
        USES_API_KEY: data.USES_API_KEY || 'N',
        uapi_enabled: data.uapi_enabled || 'N',
        IS_VENDOR: data.IS_VENDOR || 'N',
        is_integration_source: data.is_integration_source || 'N',
        uses_proxy: data.uses_proxy || 'N',
        has_file_viewer: data.has_file_viewer === 'Y' ? 'Y' : 'N',
        is_websocket_client: data.is_websocket_client === 'Y' ? 'Y' : 'N',
        uses_streaming_client: data.uses_streaming_client === 'Y' ? 'Y' : 'N',
        streaming_container: data.streaming_container || '',
        uses_event_poller: data.uses_event_poller === 'Y' ? 'Y' : 'N',
        run_now_button: data.run_now_button === 'Y' ? 'Y' : 'N',
        has_pipeline_conversions: data.has_pipeline_conversions === 'Y' ? 'Y' : 'N',
        polling_implementation_id: data.polling_implementation_id || '',
        proxy_name: data.proxy_name || '',
        authentication_scheme: data.authentication_scheme || 'NONE',
        marketplace_to_hub: data.marketplace_to_hub || 'NONE',
        hub_to_marketplace: data.hub_to_marketplace || 'NONE',
        result_viewer: data.result_viewer || '',
        syncconnect_repo_ref: data.syncconnect_repo_ref || '',
        LATESTVERSION: data.LATESTVERSION || '',
        payment_porter: data.payment_porter || 'N',
        has_enqueue: data.has_enqueue || 'N',
        has_dequeue: data.has_dequeue || 'N',
        api_transactions_per_unit: data.api_transactions_per_unit ?? 10000,
        api_cost_per_unit: data.api_cost_per_unit ?? 0,
        api_cost_currency: data.api_cost_currency || 'EUR'
      });

      await fetchMarketplaceSettings();
      await fetchModules();
      await fetchApiDocs();
      await fetchActions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch marketplace item');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketplaceSettings = async () => {
    try {
      const response = await marketplacesettingsService.getAllSettings({ MARKETPLACEID: id });
      setMarketplaceSettings(response.settings || []);
    } catch (err) {
      console.error('Error fetching marketplace settings:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    if (!formData.SYSTEMNAME) {
      setError('System name is required');
      return;
    }

    setSaving(true);
    try {
      if (isCreateMode) {
        await marketplaceService.createSetting(formData);
        navigate('/admin/marketplace');
        return;
      }
      // UPDATE STAYS ON THE TAB IT WAS PRESSED ON. Edits here come in runs — a flag on Adapter,
      // then another — and being thrown back to the list meant finding the system and the tab again.
      await marketplaceService.updateSetting(id, formData);
      setSaved(true);
    } catch (err) {
      console.error('Error saving marketplace item:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save marketplace item');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/marketplace');
  };

  if (loading && isEditMode) {
    return <div className="connector-form-container">Loading...</div>;
  }

  return (
    <div className="connector-form-container">
      <button
        type="button"
        className="btn-secondary"
        onClick={() => navigate('/admin/marketplace')}
        style={{ marginBottom: '1rem' }}
      >
        ← Back to Marketplaces
      </button>
      <h2>
        {isCreateMode && 'Create New Marketplace Item'}
        {isEditMode && `Edit Marketplace Item${id ? ` (ID: ${id})` : ''}`}
      </h2>

      {error && <div className="error-message">{error}</div>}

      {/* Universal API on/off for this system. Above the tabs because it governs the whole record, not
          one tab's worth of it — and because "is this offered at all?" is read before anything else.

          Saved with the rest of the form, not on click: a switch that writes immediately would be the
          only control on this page that does, and there would be no way to change your mind. */}
      <div className="mkt-flag-row">
        <div className={`mkt-uapi-toggle${formData.uapi_enabled === 'Y' ? ' mkt-uapi-toggle-on' : ''}`}>
          <label className="mkt-uapi-switch">
            <input
              type="checkbox"
              checked={formData.uapi_enabled === 'Y'}
              onChange={(e) => setFormData({ ...formData, uapi_enabled: e.target.checked ? 'Y' : 'N' })}
            />
            <span className="mkt-uapi-slider" />
          </label>
          <div className="mkt-uapi-text">
            <strong>Universal API</strong>
            <span>
              {formData.uapi_enabled === 'Y'
                ? 'This system is offered through the Universal API. The Modules tab decides which resource paths it serves.'
                : 'Off — this system is not offered through the Universal API, whatever modules it has.'}
            </span>
          </div>
        </div>

        {/* WHO MAY BE ISSUED CREDENTIALS FOR THIS SYSTEM (IS_VENDOR, V504).
            Beside the Universal API switch because it answers the neighbouring question — that one is
            "do we offer this system", this one is "may an outside company hold credentials for it" — and
            because both govern the whole record rather than one tab's worth of it.
            A separate flag, not a consequence of the first: a system can be offered through the Universal
            API without any vendor being given its own credentials, and the Vendors screen offers exactly
            the systems somebody has said yes to here. */}
        <div className={`mkt-uapi-toggle${formData.IS_VENDOR === 'Y' ? ' mkt-uapi-toggle-on' : ''}`}>
          <label className="mkt-uapi-switch">
            <input
              type="checkbox"
              checked={formData.IS_VENDOR === 'Y'}
              onChange={(e) => setFormData({ ...formData, IS_VENDOR: e.target.checked ? 'Y' : 'N' })}
            />
            <span className="mkt-uapi-slider" />
          </label>
          <div className="mkt-uapi-text">
            <strong>Vendor system</strong>
            <span>
              {formData.IS_VENDOR === 'Y'
                ? 'Vendors can be issued partner-level credentials for this system. It is offered when adding a vendor under Partner Settings.'
                : 'Off — this system is not offered when adding a vendor.'}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          type="button"
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          System Information
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'company' ? 'active' : ''}`}
          onClick={() => setActiveTab('company')}
        >
          Company
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
          onClick={() => setActiveTab('description')}
        >
          Description
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Configuration
        </button>
        {isEditMode && (
          <button
            type="button"
            className={`tab-button ${activeTab === 'marketplacesettings' ? 'active' : ''}`}
            onClick={() => setActiveTab('marketplacesettings')}
          >
            Marketplace Settings ({marketplaceSettings.length})
          </button>
        )}
        {isEditMode && (
          <button
            type="button"
            className={`tab-button ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('modules')}
          >
            Modules ({modules.length})
          </button>
        )}
        {isEditMode && (
          <button
            type="button"
            className={`tab-button ${activeTab === 'esb' ? 'active' : ''}`}
            onClick={() => setActiveTab('esb')}
          >
            Adapter ({esbAttributes.length})
          </button>
        )}
        {isEditMode && (
          <button
            type="button"
            className={`tab-button ${activeTab === 'apidoc' ? 'active' : ''}`}
            onClick={() => setActiveTab('apidoc')}
          >
            API Doc ({apiDocs.length})
          </button>
        )}
        {isEditMode && (
          <button
            type="button"
            className={`tab-button ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            Actions ({actions.length})
          </button>
        )}
        <button
          type="button"
          className={`tab-button ${activeTab === 'transactionfees' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactionfees')}
        >
          Transaction Fees
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="form-section">
            <div className="form-group">
            <label htmlFor="SYSTEMNAME">System Name *</label>
            <input
              type="text"
              id="SYSTEMNAME"
              name="SYSTEMNAME"
              value={formData.SYSTEMNAME}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="SYSTEMROLE">System Role</label>
            <input
              type="text"
              id="SYSTEMROLE"
              name="SYSTEMROLE"
              value={formData.SYSTEMROLE}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="SYSTEMURLPARAMETER">System URL Parameter</label>
            <input
              type="text"
              id="SYSTEMURLPARAMETER"
              name="SYSTEMURLPARAMETER"
              value={formData.SYSTEMURLPARAMETER}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="SYSTEMSTATUS">Status</label>
            <select
              id="SYSTEMSTATUS"
              name="SYSTEMSTATUS"
              value={formData.SYSTEMSTATUS}
              onChange={handleChange}
            >
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="SYSTEMACTIVE">Active</label>
            <select
              id="SYSTEMACTIVE"
              name="SYSTEMACTIVE"
              value={formData.SYSTEMACTIVE}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="LATESTVERSION">Latest Version</label>
            <input
              type="text"
              id="LATESTVERSION"
              name="LATESTVERSION"
              value={formData.LATESTVERSION}
              onChange={handleChange}
            />
          </div>
          </div>
        )}

        {/* Company Tab */}
        {activeTab === 'company' && (
          <div className="form-section">
            <div className="form-group">
            <label htmlFor="COMPANYNAME">Company Name</label>
            <input
              type="text"
              id="COMPANYNAME"
              name="COMPANYNAME"
              value={formData.COMPANYNAME}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="COMPANYWEB">Company Website</label>
            <input
              type="text"
              id="COMPANYWEB"
              name="COMPANYWEB"
              value={formData.COMPANYWEB}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="COMPANYSUPPORTEMAIL">Support Email</label>
            <input
              type="email"
              id="COMPANYSUPPORTEMAIL"
              name="COMPANYSUPPORTEMAIL"
              value={formData.COMPANYSUPPORTEMAIL}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="COMPANYSUPPORTWEB">Support Website</label>
            <input
              type="text"
              id="COMPANYSUPPORTWEB"
              name="COMPANYSUPPORTWEB"
              value={formData.COMPANYSUPPORTWEB}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="SYSTEMIMAGEURL">System Image URL</label>
            <input
              type="text"
              id="SYSTEMIMAGEURL"
              name="SYSTEMIMAGEURL"
              value={formData.SYSTEMIMAGEURL}
              onChange={handleChange}
            />
          </div>
          </div>
        )}

        {/* Description Tab */}
        {activeTab === 'description' && (
          <div className="form-section">
            <div className="form-group full-width">
            <label htmlFor="SYSTEMSUBTITLE">Subtitle</label>
            <input
              type="text"
              id="SYSTEMSUBTITLE"
              name="SYSTEMSUBTITLE"
              value={formData.SYSTEMSUBTITLE}
              onChange={handleChange}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="SYSTEMDESCRIPTION">Description</label>
            <textarea
              id="SYSTEMDESCRIPTION"
              name="SYSTEMDESCRIPTION"
              value={formData.SYSTEMDESCRIPTION}
              onChange={handleChange}
              rows="4"
            />
          </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="form-section">
            <div className="form-group">
            <label htmlFor="ALLOWTEST">Allow Test</label>
            <select
              id="ALLOWTEST"
              name="ALLOWTEST"
              value={formData.ALLOWTEST}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="IS_CLOUD_TESTABLE">Is Cloud Testable</label>
            <select
              id="IS_CLOUD_TESTABLE"
              name="IS_CLOUD_TESTABLE"
              value={formData.IS_CLOUD_TESTABLE}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="ALLOWCREATE">Allow Create</label>
            <select
              id="ALLOWCREATE"
              name="ALLOWCREATE"
              value={formData.ALLOWCREATE}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="HASSETTINGS">Has Settings</label>
            <select
              id="HASSETTINGS"
              name="HASSETTINGS"
              value={formData.HASSETTINGS}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="USES_API_KEY">Uses API Key</label>
            <select
              id="USES_API_KEY"
              name="USES_API_KEY"
              value={formData.USES_API_KEY}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          {/* SYNCCONNECT BUILD (V595) — the syncconnect branch, tag or commit that Generate SyncConnect builds for a
              project whose source is this system (SC_REPO_REF). Blank = the build's default (dev). */}
          <div className="form-group">
            <label htmlFor="syncconnect_repo_ref">SyncConnect Branch</label>
            <input
              type="text"
              id="syncconnect_repo_ref"
              name="syncconnect_repo_ref"
              value={formData.syncconnect_repo_ref}
              onChange={handleChange}
              placeholder="dev"
            />
          </div>

          <div className="form-group">
            <label htmlFor="payment_porter">Payment Porter</label>
            <select
              id="payment_porter"
              name="payment_porter"
              value={formData.payment_porter}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="has_enqueue">Has Enqueue</label>
            <select
              id="has_enqueue"
              name="has_enqueue"
              value={formData.has_enqueue}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="has_dequeue">Has Dequeue</label>
            <select
              id="has_dequeue"
              name="has_dequeue"
              value={formData.has_dequeue}
              onChange={handleChange}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>
          </div>
        )}

        {/* Marketplace Settings Tab */}
        {activeTab === 'marketplacesettings' && isEditMode && (
          <div className="connector-subsection">
            <div className="subsection-header">
              <h3>Marketplace Settings ({marketplaceSettings.length})</h3>
              <button
                type="button"
                className="btn-primary"
                onClick={() => navigate(`/admin/marketplace/${id}/setting/new`)}
              >
                Add Setting
              </button>
            </div>

            {marketplaceSettings.length === 0 ? (
              <p className="no-items">No settings configured for this marketplace item.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Setting Name</th>
                    <th>Type</th>
                    <th>Sub Group</th>
                    <th>Required</th>
                    <th>Setting Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {marketplaceSettings.map((setting) => (
                    <tr
                      key={setting.MARKETPLACESETTINGID}
                      className="clickable-row"
                      onClick={() => navigate(`/admin/marketplace/${id}/setting/${setting.MARKETPLACESETTINGID}`)}
                      title="Click to view"
                    >
                      <td>{setting.MARKETPLACESETTINGID}</td>
                      <td>{setting.SETTINGNAME}</td>
                      <td>{setting.type || ''}</td>
                      <td>{setting.SUB_GROUP || ''}</td>
                      <td>{setting.required === 'Y' ? 'Yes' : 'No'}</td>
                      <td className="setting-value">
                        {setting.SETTINGVALUE ? (setting.SETTINGVALUE.length > 50 ? setting.SETTINGVALUE.substring(0, 50) + '...' : setting.SETTINGVALUE) : 'N/A'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/marketplace/${id}/setting/${setting.MARKETPLACESETTINGID}/edit`);
                          }}
                          className="btn-small"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Delete setting "${setting.SETTINGNAME}"?`)) return;
                            try {
                              await marketplacesettingsService.deleteSetting(setting.MARKETPLACESETTINGID);
                              setMarketplaceSettings(prev => prev.filter(s => s.MARKETPLACESETTINGID !== setting.MARKETPLACESETTINGID));
                            } catch (err) {
                              alert(err.response?.data?.message || 'Failed to delete setting');
                            }
                          }}
                          className="btn-small"
                          title="Delete"
                          style={{ marginLeft: '4px', color: '#dc2626' }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Modules Tab */}
        {/* ── ESB INTEGRATION ──────────────────────────────────────────────────────────────
            What this system brings to an integration, so the ESB screen can compose one: whether it can
            be the source of a record, the target of one, or both, which records it deals in, and
            whatever else that decision turns out to need.

            FACTS ABOUT THE SYSTEM, NOT ABOUT AN INTEGRATION. Every integration involving this system
            asks the same questions of it, so the answers are stored once, here, rather than repeated
            in each one and left to drift apart.

            NOT CREDENTIAL FIELDS. Marketplace Settings defines what somebody must type to CONNECT to
            this system. This says what the system can DO once connected — a different question with
            different readers. */}
        {activeTab === 'esb' && isEditMode && (
          <div className="connector-subsection">
            <div className="subsection-header">
              <h3>Adapter ({esbAttributes.length})</h3>
            </div>
            {/* CAN THIS SYSTEM BE THE SOURCE END? The first thing the ESB screen has to know about a
                system is which end of a join it can sit on, so it is a flag on the record rather than
                one attribute among the rest — every system has an answer, and "nobody has written a
                row yet" is not one of them.

                SAVED WITH THE FORM, not on the click. It is a field of the marketplace like any other,
                and a control that wrote immediately would be the only one on this screen that did. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="is_integration_source">Is Integration Source</label>
              {/* A Yes/No select, like every other flag on this form. A checkbox here would be the only
                  one, and "unticked" and "No" do not read as the same answer when they sit next to
                  each other. */}
              <select
                id="is_integration_source"
                name="is_integration_source"
                value={formData.is_integration_source}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
              <p className="form-help">
                {formData.is_integration_source === 'Y'
                  ? 'This system can be the FROM end of an integration — it emits records for another '
                    + 'system to receive.'
                  : 'This system is not offered as the FROM end of an integration. Every system starts '
                    + 'here; somebody says otherwise one system at a time.'}
              </p>
            </div>

            {/* IS WEBSOCKET CLIENT (V536) — only asked of an integration source; N for every existing system.
                Kept (not cleared) when the source flag is switched off, like proxy_name. */}
            {formData.is_integration_source === 'Y' && (
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="is_websocket_client">Is Websocket Client</label>
                <select
                  id="is_websocket_client"
                  name="is_websocket_client"
                  value={formData.is_websocket_client}
                  onChange={handleChange}
                >
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>
            )}

            {/* USES STREAMING CLIENT (V616) — the system's messages are received by a streaming consumer the hub runs
                per implementation (OHIP Streaming → ohip-events on EKS). A pipeline implementation whose source has it
                offers "Deploy Streaming Client". N for every existing system. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="uses_streaming_client">Uses Streaming Client</label>
              <select
                id="uses_streaming_client"
                name="uses_streaming_client"
                value={formData.uses_streaming_client}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
              <p className="form-help">
                {formData.uses_streaming_client === 'Y'
                  ? 'Messages from this system arrive through a streaming client the hub runs for each pipeline '
                    + 'implementation; implementations offer "Deploy Streaming Client".'
                  : 'This system sends its messages to the hub itself; no streaming client is deployed.'}
              </p>
            </div>

            {/* USES EVENT POLLER (V635) — the hub can poll this system's business events itself (the OHIP Adapter,
                code-pipelines' event poller). A pipeline implementation whose source has it offers "Event poller". */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="uses_event_poller">Uses Event Poller</label>
              <select
                id="uses_event_poller"
                name="uses_event_poller"
                value={formData.uses_event_poller}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
              <p className="form-help">
                {formData.uses_event_poller === 'Y'
                  ? 'The hub can poll this system\u2019s business events for a pipeline implementation; implementations '
                    + 'offer "Event poller".'
                  : 'The hub does not poll this system for events.'}
              </p>
            </div>

            {/* RUN NOW BUTTON (V658) — the hub runs this system itself on a schedule and it can also be started by hand
                (the Crawler: one crawl is a Step Functions execution). */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="run_now_button">Run Now Button</label>
              <select
                id="run_now_button"
                name="run_now_button"
                value={formData.run_now_button}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
              <p className="form-help">
                {formData.run_now_button === 'Y'
                  ? 'The hub runs this system on its schedule, and an implementation can also start a run by hand with "Run now".'
                  : 'Runs only when the system sends, or on its schedule — there is no Run now.'}
              </p>
            </div>

            {/* HAS PIPELINE CONVERSIONS (V721) — a pipeline implementation whose adapter is this system gets a Conversions
                tab (record type / field / source value → target value, the sync Conversions screen's vocabulary), applied
                by the adapter to every record through the shared code field map. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="has_pipeline_conversions">Has Pipeline Conversions</label>
              <select
                id="has_pipeline_conversions"
                name="has_pipeline_conversions"
                value={formData.has_pipeline_conversions}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
              <p className="form-help">
                {formData.has_pipeline_conversions === 'Y'
                  ? 'A pipeline implementation offers a Conversions tab for this adapter: code values (room types, rate codes, '
                    + 'market codes, languages …) converted per property before each record is sent on.'
                  : 'Records are sent on with their codes as the source wrote them; there is no Conversions tab.'}
              </p>
            </div>

            {/* POLLING IMPLEMENTATION (V705) — which processing step resyncs this system's adapter. The step's NAME is a
                Step Functions state machine (admin › Processing Steps); it writes its progress to OpenSearch so the
                resync modal can show which step it is on. Set, an implementation of this system offers "Resync"; None,
                there is no resync. The OHIP Adapter uses ohip-adapter-resync-process. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="polling_implementation_id">Polling Implementation</label>
              <select
                id="polling_implementation_id"
                name="polling_implementation_id"
                value={formData.polling_implementation_id}
                onChange={handleChange}
              >
                <option value="">None — no resync for this system</option>
                {processingSteps.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <p className="form-help">
                {formData.polling_implementation_id
                  ? 'An implementation of this system offers "Resync": a user picks reservations, profiles or blocks and '
                    + 'a date range, and this processing step’s Step Functions state machine asks the source for that '
                    + 'history again, reporting its progress as it runs.'
                  : 'This system has no resync. Choose a processing step to give its implementations a Resync button.'}
              </p>
            </div>

            {/* STREAMING CONTAINER (V618) — WHICH streaming client: containers/<name>, pushed to ECR as
                <env>-<region>-<name> and listed in code-eks' streaming_containers. Only asked when the system uses one;
                the lambda requires it then. Kept when the flag is switched off, so switching back loses nothing. */}
            {formData.uses_streaming_client === 'Y' && (
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="streaming_container">Streaming Container</label>
                <input
                  type="text"
                  id="streaming_container"
                  name="streaming_container"
                  value={formData.streaming_container}
                  onChange={handleChange}
                  placeholder="ohip-events"
                  maxLength={41}
                  pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
                  required
                />
                <p className="form-help">
                  The container each implementation of this system runs (containers/&lt;name&gt; in the repo, its own
                  ECR repository in code-eks). Lower-case letters, digits and hyphens. ohip-events is the OHIP Streaming
                  client; a different name deploys a different client, given this system&rsquo;s credential settings.
                </p>
              </div>
            )}

            {/* DOES IT NEED AN ADAPTER, AND WHAT IS IT CALLED?
                A system does not speak to the Universal API in its own protocol — a microservice in
                front of it receives what the system sends and calls the API. The designer draws that
                box from these two fields rather than asking per integration: every integration
                involving this system puts the same proxy in front of it. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="uses_proxy">Uses Proxy</label>
              <select
                id="uses_proxy"
                name="uses_proxy"
                value={formData.uses_proxy}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

            {/* SHOWN ONLY WHEN IT IS NEEDED. A proxy name on a system that uses none is a value nothing
                reads, and it would still be there — and still wrong — if the flag were switched back. */}
            {formData.uses_proxy === 'Y' && (
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="proxy_name">Proxy Name</label>
                <input
                  type="text"
                  id="proxy_name"
                  name="proxy_name"
                  value={formData.proxy_name}
                  onChange={handleChange}
                  placeholder="e.g. Opera OXI Proxy"
                />
                <p className="form-help">
                  The microservice deployed in front of this system. The integration designer draws it
                  as a box between this system and the Universal API, so the name should be the one that
                  is actually deployed.
                </p>
              </div>
            )}

            {/* HAS FILE VIEWER (V534) — Y/N on the system's own record; N for every existing one. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="has_file_viewer">Has File Viewer</label>
              <select
                id="has_file_viewer"
                name="has_file_viewer"
                value={formData.has_file_viewer}
                onChange={handleChange}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

            {/* HOW AN INTEGRATION AUTHENTICATES TO THIS SYSTEM. A fact about the system, like the two
                fields above: every integration involving it authenticates the same way. The option's
                value is the code stored in marketplace.authentication_scheme; the text is only what is
                shown, so it can be reworded without touching data. None is the default — new schemes
                are added to AUTHENTICATION_SCHEMES. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="authentication_scheme">Authentication Scheme</label>
              <select
                id="authentication_scheme"
                name="authentication_scheme"
                value={formData.authentication_scheme}
                onChange={handleChange}
              >
                {AUTHENTICATION_SCHEMES.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* HOW DATA MOVES, ONE SELECT PER DIRECTION (V488). A system may push to the hub and never be
                pulled from, so the two are separate answers. Options live in MARKETPLACE_TO_HUB and
                HUB_TO_MARKETPLACE; None is the default for both. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="marketplace_to_hub">Marketplace to Hub</label>
              <select
                id="marketplace_to_hub"
                name="marketplace_to_hub"
                value={formData.marketplace_to_hub}
                onChange={handleChange}
              >
                {MARKETPLACE_TO_HUB.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="hub_to_marketplace">Hub to Marketplace</label>
              <select
                id="hub_to_marketplace"
                name="hub_to_marketplace"
                value={formData.hub_to_marketplace}
                onChange={handleChange}
              >
                {HUB_TO_MARKETPLACE.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* RESULT VIEWER (V583) — what the Target card on the pipeline board opens to show this system's
                result. File viewer browses the target's files (Azure Blob). Options live in RESULT_VIEWERS. */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="result_viewer">Result Viewer</label>
              <select
                id="result_viewer"
                name="result_viewer"
                value={formData.result_viewer}
                onChange={handleChange}
              >
                {RESULT_VIEWERS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>


            <p className="form-help" style={{ marginBottom: '1rem' }}>
              Read by the ESB screen when it composes an integration. An attribute applies in one
              direction or both: a system may emit reservations and accept none, and an attribute that
              could not say which way it applied would have to be read twice and believed once.
            </p>

            <div className="inline-add-row">
              <div className="field grow">
                <label htmlFor="newEsbAttribute">Attribute</label>
                <input
                  type="text"
                  id="newEsbAttribute"
                  value={newEsbAttribute.attribute}
                  onChange={(e) => setNewEsbAttribute({ ...newEsbAttribute, attribute: e.target.value })}
                  placeholder="e.g. supports-reservations"
                />
              </div>
              <div className="field grow">
                <label htmlFor="newEsbValue">Value</label>
                <input
                  type="text"
                  id="newEsbValue"
                  value={newEsbAttribute.value}
                  onChange={(e) => setNewEsbAttribute({ ...newEsbAttribute, value: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="newEsbDirection">Direction</label>
                <select
                  id="newEsbDirection"
                  value={newEsbAttribute.direction}
                  onChange={(e) => setNewEsbAttribute({ ...newEsbAttribute, direction: e.target.value })}
                >
                  <option value="BOTH">Both ways</option>
                  <option value="SOURCE">As a source</option>
                  <option value="TARGET">As a target</option>
                </select>
              </div>
              <div className="field grow">
                <label htmlFor="newEsbNotes">Notes (optional)</label>
                <input
                  type="text"
                  id="newEsbNotes"
                  value={newEsbAttribute.notes}
                  onChange={(e) => setNewEsbAttribute({ ...newEsbAttribute, notes: e.target.value })}
                  placeholder="Why this value is what it is"
                />
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => saveEsbAttribute(newEsbAttribute)}
                disabled={!newEsbAttribute.attribute.trim()}
              >
                Add Attribute
              </button>
            </div>

            {esbAttributes.length === 0 ? (
              <p className="no-items">
                Nothing recorded yet. Until there is, the ESB screen has nothing to compose an
                integration for this system from.
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Attribute</th>
                    <th>Value</th>
                    <th>Direction</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {esbAttributes.map((attr) => (
                    <tr key={attr.id}>
                      <td><strong>{attr.attribute}</strong></td>
                      <td>{attr.value || <span className="text-muted">—</span>}</td>
                      <td>
                        {attr.direction === 'BOTH' ? 'Both ways'
                          : attr.direction === 'SOURCE' ? 'As a source' : 'As a target'}
                      </td>
                      <td>{attr.notes || <span className="text-muted">—</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn-small"
                          style={{ color: '#dc2626' }}
                          title={`Delete "${attr.attribute}"`}
                          onClick={() => removeEsbAttribute(attr.id)}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'modules' && isEditMode && (
          <div className="connector-subsection">
            <div className="subsection-header">
              <h3>Modules ({modules.length})</h3>
            </div>
            {/* A module IS a Universal API resource path, chosen from the routes that exist rather than
                typed. A free-text "Reservation" could never be matched to /rsv without a translation
                table, which is the thing this replaces. The label is optional and defaults to the path. */}
            <div className="inline-add-row">
              <div className="field grow">
                <label htmlFor="newModuleRoute">Universal API module</label>
                <select
                  id="newModuleRoute"
                  value={newModuleRouteId}
                  onChange={(e) => setNewModuleRouteId(e.target.value)}
                >
                  <option value="">-- Select a module --</option>
                  {uapiRoutes
                    .filter((r) => !modules.some((m) => String(m.uapi_route_id) === String(r.id)))
                    .map((r) => (
                      <option key={r.id} value={r.id}>{r.path}</option>
                    ))}
                </select>
              </div>
              <div className="field grow">
                <label htmlFor="newModuleName">Label (optional)</label>
                <input
                  type="text"
                  id="newModuleName"
                  value={newModuleName}
                  onChange={(e) => setNewModuleName(e.target.value)}
                  placeholder="defaults to the path, e.g. rsv"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addModule(); } }}
                />
              </div>
              <button type="button" className="btn-primary" onClick={addModule} disabled={!newModuleRouteId}>Add Module</button>
            </div>
            {modules.length === 0 ? (
              <p className="no-items">No modules for this marketplace.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Module</th><th>Label</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m.id}>
                      {/* A module with no route predates V416 and is not offered through the Universal
                          API — said plainly rather than shown as a blank cell. */}
                      <td>{m.uapi_route_path || <span className="text-muted">not mapped</span>}</td>
                      <td>{m.name}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn-small"
                          style={{ color: '#dc2626' }}
                          title="Delete"
                          onClick={() => setConfirmDelete({ kind: 'module', id: m.id, label: m.name })}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* API Doc Tab */}
        {activeTab === 'apidoc' && isEditMode && (
          <div className="connector-subsection">
            <div className="subsection-header">
              <h3>API Doc ({apiDocs.length})</h3>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
              Where the API documentation lives — a URL, a file link, or a document written here.
              A document is what the UAPI Integrations assistant maintains as it learns this backend,
              and what it reads back for context on the next question.
            </p>
            <div className="inline-add-row">
              <div className="field">
                <label>Label</label>
                <input
                  type="text"
                  value={newApiDoc.label}
                  onChange={(e) => setNewApiDoc(p => ({ ...p, label: e.target.value }))}
                  placeholder="e.g. OHIP Reservation API"
                />
              </div>
              <div className="field">
                <label>Type</label>
                <select value={newApiDoc.doc_type} onChange={(e) => setNewApiDoc(p => ({ ...p, doc_type: e.target.value }))}>
                  <option value="url">URL</option>
                  <option value="file">File</option>
                  <option value="markdown">Document</option>
                </select>
              </div>
              {newApiDoc.doc_type === 'markdown' ? (
                <div className="field grow">
                  <label>Document</label>
                  <textarea
                    rows={4}
                    value={newApiDoc.content}
                    onChange={(e) => setNewApiDoc(p => ({ ...p, content: e.target.value }))}
                    placeholder="Markdown. What is known about this backend — auth, operations, error codes, traps."
                    style={{ width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' }}
                  />
                </div>
              ) : (
                <div className="field grow">
                  <label>Location (URL or file link)</label>
                  <input
                    type="text"
                    value={newApiDoc.location}
                    onChange={(e) => setNewApiDoc(p => ({ ...p, location: e.target.value }))}
                    placeholder="https://... or a file link"
                  />
                </div>
              )}
              <button type="button" className="btn-primary" onClick={addApiDoc}>Add</button>
            </div>
            {apiDocs.length === 0 ? (
              <p className="no-items">No API documentation links yet.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Label</th><th>Type</th><th>Location</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {apiDocs.map((d) => (
                    <tr key={d.id}>
                      <td>{d.label}</td>
                      <td style={{ textTransform: 'uppercase', fontSize: '0.8rem' }}>{d.doc_type}</td>
                      <td className="setting-value">
                        {d.doc_type === 'markdown' ? (
                          <button
                            type="button"
                            className="btn-small"
                            onClick={() => { setOpenDoc(d); setDocText(d.content || ''); }}
                          >
                            {openDoc && openDoc.id === d.id ? 'Editing…' : 'Open document'}
                            <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                              {(d.content || '').length.toLocaleString()} chars
                            </span>
                          </button>
                        ) : (
                          <a href={d.location} target="_blank" rel="noopener noreferrer">{d.location}</a>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn-small"
                          style={{ color: '#dc2626' }}
                          title="Delete"
                          onClick={() => setConfirmDelete({ kind: 'apidoc', id: d.id, label: d.label })}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* A plain textarea, like every other editor in this portal — the assistant writes this
                document far more often than a person does, and a markdown editor dependency for the
                occasional hand edit is not worth the bundle. */}
            {openDoc && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>
                  {openDoc.label}
                </label>
                <textarea
                  rows={22}
                  value={docText}
                  onChange={(e) => setDocText(e.target.value)}
                  spellCheck={false}
                  style={{ width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' }}
                />
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                  <button type="button" className="btn-primary" onClick={saveOpenDoc} disabled={savingDoc}>
                    {savingDoc ? 'Saving…' : 'Save document'}
                  </button>
                  <button type="button" className="btn-small" onClick={() => setOpenDoc(null)} disabled={savingDoc}>
                    Close
                  </button>
                  <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    {docText.length.toLocaleString()} chars
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && isEditMode && (
          <div className="connector-subsection">
            <div className="subsection-header">
              <h3>Actions ({actions.length})</h3>
            </div>
            <div className="inline-add-row">
              <div className="field">
                <label>Action Code</label>
                <select
                  style={{ width: 210 }}
                  value={newAction.marketplaceactiontype_id}
                  onChange={(e) => setNewAction(p => ({ ...p, marketplaceactiontype_id: e.target.value }))}
                >
                  <option value="">Select…</option>
                  {Object.entries(
                    actionTypes.reduce((acc, t) => {
                      const cat = t.category || 'Other';
                      (acc[cat] = acc[cat] || []).push(t);
                      return acc;
                    }, {})
                  ).map(([cat, list]) => (
                    <optgroup key={cat} label={cat}>
                      {list.map(t => <option key={t.id} value={t.id}>{t.code}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="field grow">
                <label>Description</label>
                <input
                  type="text"
                  value={newAction.actiondescription}
                  onChange={(e) => setNewAction(p => ({ ...p, actiondescription: e.target.value }))}
                  placeholder="What this action does"
                />
              </div>
              <div className="field">
                <label>Status</label>
                <select value={newAction.marketplaceactionstatus} onChange={(e) => setNewAction(p => ({ ...p, marketplaceactionstatus: e.target.value }))}>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
              <button type="button" className="btn-primary" onClick={addAction}>Add Action</button>
            </div>
            {actions.length === 0 ? (
              <p className="no-items">No actions for this marketplace.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Code</th><th>Description</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {actions.map((a) => (
                    <tr key={a.id}>
                      <td>{a.actioncode}</td>
                      <td className="setting-value">{a.actiondescription}</td>
                      <td>{a.marketplaceactionstatus}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn-small"
                          style={{ color: '#dc2626' }}
                          title="Delete"
                          onClick={() => setConfirmDelete({ kind: 'action', id: a.id, label: a.actioncode })}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Transaction Fees Tab */}
        {activeTab === 'transactionfees' && (
          <div className="form-section">
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              Charge customers a flat fee per N transactions. Used to estimate costs (e.g. on the vault API Settings page) before they commit.
            </p>
            <div className="form-group">
              <label htmlFor="api_cost_per_unit">Cost per unit</label>
              <input
                type="number"
                id="api_cost_per_unit"
                name="api_cost_per_unit"
                value={formData.api_cost_per_unit}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="10.00"
              />
            </div>
            <div className="form-group">
              <label htmlFor="api_cost_currency">Currency</label>
              <CurrencySelect
                billing
                id="api_cost_currency"
                name="api_cost_currency"
                value={formData.api_cost_currency}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="api_transactions_per_unit">Transactions per unit</label>
              <input
                type="number"
                id="api_transactions_per_unit"
                name="api_transactions_per_unit"
                value={formData.api_transactions_per_unit}
                onChange={handleChange}
                min="1"
                step="1"
                placeholder="10000"
              />
            </div>
            <div style={{ padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.875rem', color: '#166534' }}>
              Effective rate: <strong>{Number(formData.api_cost_per_unit || 0).toFixed(2)} {formData.api_cost_currency}</strong> per <strong>{Number(formData.api_transactions_per_unit || 0).toLocaleString()}</strong> transactions
            </div>
          </div>
        )}

        {/* Form Actions - Show on form tabs only */}
        {!['marketplacesettings', 'modules', 'apidoc', 'actions'].includes(activeTab) && (
          <div className="form-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving...' : isCreateMode ? 'Create' : 'Update'}
            </button>
            <button type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
            {saved && <span className="form-saved" role="status">Saved</span>}
          </div>
        )}
      </form>

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.kind === 'module' ? 'Delete module' : confirmDelete.kind === 'action' ? 'Delete action' : 'Delete API doc'}
          message={`Delete "${confirmDelete.label}"? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

export default MarketplaceForm;
